/**
 * ==========================================================================
 *  INTOXICATION TUNING
 * ==========================================================================
 *  Two independent axes, with starting values set before a run:
 *
 *    alcoholPromille  0.00 - 5.00 ‰   -> MOTOR control (response, grip, braking)
 *    drugsPercent     0 - 100 %       -> PERCEPTION   (colours, distortion)
 *
 *  The split is deliberate. Booze decides where you actually end up; acid only
 *  decides what the trip looks like. That keeps the two sliders legible, and it
 *  means the psychedelic layer can never be a hidden movement handicap.
 *
 *  Sips raise the live alcohol level during the run. Sampling remains a pure
 *  function of simulation time and dose, with no Math.random() or frame clock.
 * ==========================================================================
 */

import { clamp, type Vec2 } from '../utils/math';
import { mulberry32, randRange } from '../utils/prng';

/** One sine term: amp * sin(t * freq + phase). */
export interface SwayComponent {
  freq: number;
  amp: number;
  phase: number;
}

/** A render-only camera lean and scuff at a known point in the run. */
export interface StumbleEvent {
  /** Simulation time (seconds) at which the scuff begins. */
  startTime: number;
  /** Length of the cosmetic accent in seconds. */
  duration: number;
  /** Local direction (x = right, y = forward). Normalised on load. */
  direction: Vec2;
  /** Peak camera/audio intensity at the reference dose. */
  strength: number;
}

/* ------------------------------------------------------- dose settings --- */

export interface IntoxicationLevel {
  /** Blood alcohol, promille. 0.00 - 5.00, adjustable in 0.01 steps. */
  alcoholPromille: number;
  /** Psychedelic dose, 0 - 100 %. */
  drugsPercent: number;
}

export const INTOXICATION_LIMITS = {
  alcoholPromille: { min: 0, max: 5, step: 0.01 },
  drugsPercent: { min: 0, max: 100, step: 1 },
} as const;

/** The dose the hand-tuned baseline feel was authored against. */
export const REFERENCE_PROMILLE = 1.2;

/**
 * The dose the movement tuning, the camera accents and every number in the
 * README's bot table were written against. `ReferenceBot` and the tests that
 * price the route run here, deliberately rather than at whatever the settings
 * happen to default to - otherwise changing what a *new player* starts with
 * would quietly rewrite what the level is measured against.
 */
export const REFERENCE_INTOXICATION: IntoxicationLevel = {
  alcoholPromille: REFERENCE_PROMILLE,
  drugsPercent: 0,
};

/**
 * What somebody who has never touched the sliders gets: stone cold sober.
 *
 * The dose is the most interesting setting in the game and also the only one
 * that can make it unreadable before you know where the doorways are, so the
 * first run is the level and nothing else. Nothing is missing at 0.00 ‰ -
 * ground grip is unchanged and the cosmetic alcohol effects are silent -
 * and turning it up is the second thing you do, once the route is in your
 * hands. This is not hashed: the dose travels in the replay header, so moving
 * it retires nothing.
 */
export const DEFAULT_INTOXICATION: IntoxicationLevel = {
  alcoholPromille: 0,
  drugsPercent: 0,
};

export function sanitiseLevel(level: Partial<IntoxicationLevel>): IntoxicationLevel {
  const alcohol = clamp(
    Number(level.alcoholPromille ?? DEFAULT_INTOXICATION.alcoholPromille),
    INTOXICATION_LIMITS.alcoholPromille.min,
    INTOXICATION_LIMITS.alcoholPromille.max,
  );
  const drugs = clamp(
    Number(level.drugsPercent ?? DEFAULT_INTOXICATION.drugsPercent),
    INTOXICATION_LIMITS.drugsPercent.min,
    INTOXICATION_LIMITS.drugsPercent.max,
  );
  return {
    // Snap to the documented precision so two "identical" doses really are.
    alcoholPromille: Math.round(alcohol * 100) / 100,
    drugsPercent: Math.round(drugs),
  };
}

/** 1.0 at the reference dose, 0 when stone cold sober, ~4.2 at 5 promille. */
export function alcoholFactor(level: IntoxicationLevel): number {
  return sanitiseLevel(level).alcoholPromille / REFERENCE_PROMILLE;
}

export function drugFactor(level: IntoxicationLevel): number {
  return sanitiseLevel(level).drugsPercent / 100;
}

/**
 * Runs at different doses are different games, so they get different
 * leaderboards. This key is what the score repository buckets by.
 */
export function categoryKey(level: IntoxicationLevel): string {
  const safe = sanitiseLevel(level);
  return `a${safe.alcoholPromille.toFixed(2)}-d${safe.drugsPercent}`;
}

export function describeIntoxication(level: IntoxicationLevel): string {
  const safe = sanitiseLevel(level);
  return `Alkoholi: ${safe.alcoholPromille.toFixed(2)} ‰. Muut päihteet: ${safe.drugsPercent} %`;
}

/* ------------------------------------------------------ cosmetic accents - */

/** Render-only camera and audio accents. They never change movement. */
const AUTHORED_STUMBLES: StumbleEvent[] = [
  { startTime: 2.6, duration: 0.55, direction: { x: 1, y: 0.15 }, strength: 0.8 },
  { startTime: 6.4, duration: 0.7, direction: { x: -1, y: 0.1 }, strength: 0.95 },
  { startTime: 10.1, duration: 0.5, direction: { x: 0.85, y: -0.3 }, strength: 0.7 },
  { startTime: 14.3, duration: 0.75, direction: { x: -0.9, y: 0.35 }, strength: 0.95 },
  { startTime: 18.0, duration: 0.45, direction: { x: 1, y: 0.05 }, strength: 0.6 },
  { startTime: 22.2, duration: 0.8, direction: { x: 1, y: 0.4 }, strength: 1.1 },
  { startTime: 26.7, duration: 0.6, direction: { x: -1, y: 0.2 }, strength: 0.85 },
  { startTime: 30.4, duration: 0.5, direction: { x: 0.6, y: 0.6 }, strength: 0.7 },
  { startTime: 34.9, duration: 0.7, direction: { x: -1, y: -0.2 }, strength: 1.0 },
  { startTime: 38.6, duration: 0.55, direction: { x: 1, y: 0.25 }, strength: 0.8 },
  { startTime: 42.8, duration: 0.75, direction: { x: -0.8, y: 0.4 }, strength: 1.05 },
  { startTime: 47.1, duration: 0.5, direction: { x: 1, y: -0.15 }, strength: 0.7 },
  { startTime: 51.3, duration: 0.65, direction: { x: -1, y: 0.3 }, strength: 0.9 },
  { startTime: 55.8, duration: 0.6, direction: { x: 0.9, y: 0.35 }, strength: 0.85 },
  { startTime: 60.2, duration: 0.8, direction: { x: -1, y: 0.15 }, strength: 1.1 },
];

/**
 * Beyond the authored window we extend the schedule from a *fixed seed*, so it
 * is still byte-identical on every run and every machine. This only runs once,
 * at module load - never during the simulation.
 */
function generateTailStumbles(fromTime: number, untilTime: number, seed: number): StumbleEvent[] {
  const rng = mulberry32(seed);
  const events: StumbleEvent[] = [];
  let t = fromTime;
  let side = 1;
  while (t < untilTime) {
    t += randRange(rng, 3.2, 5.4);
    side = -side;
    events.push({
      startTime: Math.round(t * 1000) / 1000,
      duration: Math.round(randRange(rng, 0.45, 0.85) * 1000) / 1000,
      direction: {
        x: side * randRange(rng, 0.7, 1),
        y: randRange(rng, -0.3, 0.5),
      },
      strength: Math.round(randRange(rng, 0.6, 1.1) * 1000) / 1000,
    });
  }
  return events;
}

export const STUMBLE_SCHEDULE: StumbleEvent[] = [
  ...AUTHORED_STUMBLES,
  ...generateTailStumbles(62, 420, 0x1a11aa30),
].sort((a, b) => a.startTime - b.startTime);

/* --------------------------------------------------------------- booze --- */

export interface IntoxicationConfig {
  movement: {
    /** Ground grip lost per reference dose; scales friction and acceleration. */
    gripLossPerReference: number;
    /** Minimum grip at high doses, keeping braking and corrections usable. */
    minGrip: number;
    /** Added input response time as a fraction of sober timing per reference dose. */
    responseSlowdownPerReference: number;
    /** Jump button latency in seconds per reference dose. */
    jumpDelayPerReference: number;
  };
  camera: {
    scale: number;
    yaw: SwayComponent[];
    pitch: SwayComponent[];
    roll: SwayComponent[];
    bobFrequency: number;
    bobVertical: number;
    bobLateral: number;
    stumbleRoll: number;
    stumbleLead: number;
  };
}

export const INTOXICATION: IntoxicationConfig = {
  // Less ground grip preserves velocity through turns and while coasting.
  // Scale acceleration with friction so this does not raise straight-line speed.
  movement: {
    gripLossPerReference: 0.35,
    minGrip: 0.45,
    responseSlowdownPerReference: 0.5,
    jumpDelayPerReference: 0.048,
  },

  /** Render-only camera sway, independent of ground grip. */
  camera: {
    scale: 1.0,
    yaw: [
      { freq: 0.9, amp: 0.02, phase: 0.7 },
      { freq: 2.3, amp: 0.008, phase: 2.9 },
    ],
    pitch: [
      { freq: 1.31, amp: 0.014, phase: 1.9 },
      { freq: 3.7, amp: 0.005, phase: 0.2 },
    ],
    roll: [
      { freq: 0.61, amp: 0.05, phase: 3.3 },
      { freq: 1.97, amp: 0.02, phase: 0.9 },
    ],

    /** Head bob, scaled by how fast the player is actually moving. */
    bobFrequency: 11.0,
    bobVertical: 0.035,
    bobLateral: 0.028,

    /** How much a stumble leans the camera (radians per strength). */
    stumbleRoll: 0.18,
    /**
     * The camera lean starts this many seconds before the cosmetic scuff.
     */
    stumbleLead: 0.16,
  },
};

/* ----------------------------------------------------------- the acid ---- */

/**
 * LSD-flavoured layer. Values are the amount at 100 %; everything scales
 * linearly with `drugsPercent` so 0 % is a bit-for-bit plain render.
 *
 * Purely visual by design - see the header comment.
 */
export interface PsychedelicConfig {
  /** UV domain-warp amplitude (fraction of the screen). */
  warp: number;
  warpFrequency: number;
  warpSpeed: number;
  /** Chromatic separation, fraction of the screen. */
  chroma: number;
  /** Global hue rotation speed, radians per second. */
  hueSpeed: number;
  /** Extra hue shift from screen centre to edge, radians. */
  hueRadial: number;
  /** Saturation multiplier added on top of 1.0. */
  saturation: number;
  /** "Breathing" radial zoom. */
  breatheAmplitude: number;
  breatheFrequency: number;
  /** Slow whole-screen swirl, radians. */
  swirl: number;
  swirlFrequency: number;
  /** Extra camera drift, radians. Perception, not motor control. */
  cameraDriftYaw: number;
  cameraDriftPitch: number;
  cameraDriftFrequency: number;
  /** Kaleidoscope-ish edge ripple. */
  ripple: number;
  rippleFrequency: number;
}

export const PSYCHEDELIC: PsychedelicConfig = {
  warp: 0.028,
  warpFrequency: 9.0,
  warpSpeed: 1.7,
  chroma: 0.011,
  hueSpeed: 0.28,
  hueRadial: 1.5,
  saturation: 1.3,
  breatheAmplitude: 0.03,
  breatheFrequency: 0.9,
  swirl: 0.2,
  swirlFrequency: 0.37,
  cameraDriftYaw: 0.055,
  cameraDriftPitch: 0.038,
  cameraDriftFrequency: 0.41,
  ripple: 0.012,
  rippleFrequency: 23.0,
};
